import { PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { WorkOrderRecord } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersQuery";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createMediaMutation,
  deleteMediaMutation,
} from "@/app/Media/GraphQL/mediaMutations";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { useFileUpload } from "@/lib/FileUpload/Hooks/useFileUpload";
import { MediaCollection, ModelType } from "@/gql/graphql";

type AssignmentFile = WorkOrderRecord["assignments"][number]["files"][number];

interface Props {
  assignment: WorkOrderRecord["assignments"][number];
}

function getExtension(file: AssignmentFile): string {
  const name = file.file_name ?? file.name ?? "";
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toUpperCase() : "FILE";
}

function getTypeColor(file: AssignmentFile): string {
  const ext = getExtension(file).toLowerCase();
  if (ext === "pdf") return "bg-red-50 text-red-700 ring-red-200";
  if (["doc", "docx"].includes(ext))
    return "bg-blue-50 text-blue-700 ring-blue-200";
  if (["xls", "xlsx", "csv"].includes(ext))
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (["ppt", "pptx"].includes(ext))
    return "bg-orange-50 text-orange-700 ring-orange-200";
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext))
    return "bg-purple-50 text-purple-700 ring-purple-200";
  return "bg-gray-50 text-gray-700 ring-gray-200";
}

export function useAssignmentFiles(assignmentId: string) {
  const { mutateAsync: uploadFiles } = useFileUpload();
  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);
  const { mutateAsync: deleteMedia } = useGqlMutation(deleteMediaMutation);

  async function handleUpload(files: File[]) {
    if (files.length === 0) return;
    try {
      const uploads = await uploadFiles(files);
      const results = await Promise.allSettled(
        uploads.map((upload) =>
          createMedia({
            input: {
              file_name: upload.file.name,
              objectId: assignmentId,
              objectType: ModelType.WorkOrderAssignment,
              collectionName: MediaCollection.AssignmentFile,
              key: upload.filePath,
            },
          }),
        ),
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;
      if (succeeded > 0) {
        toast.success(
          succeeded === 1 ? "File uploaded" : `${succeeded} files uploaded`,
        );
      }
      if (failed > 0) {
        toast.error(
          failed === 1
            ? "Failed to upload file"
            : `Failed to upload ${failed} files`,
        );
      }
    } catch {
      toast.error("Failed to upload files");
    }
  }

  async function handleDelete(file: AssignmentFile) {
    try {
      await deleteMedia({ id: file.id });
      toast.success("File deleted");
    } catch (error) {
      toast.error("Failed to delete file");
      console.log(error);
    }
  }

  return { handleUpload, handleDelete };
}

export function AssignmentFilesPreview({ assignment }: Props) {
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { handleDelete } = useAssignmentFiles(assignment.id);
  const files = assignment.files ?? [];

  if (files.length === 0) return null;

  function confirmDelete(file: AssignmentFile) {
    openConfirmDelete({
      title: "Delete File",
      message: `Are you sure you want to delete "${file.file_name}"?`,
      onDelete: () => handleDelete(file),
    });
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="flex flex-wrap gap-x-4 gap-y-2"
    >
      {files.map((file) => {
        const ext = getExtension(file);
        return (
          <div
            key={file.id}
            className="group flex items-center gap-1.5 text-xs"
          >
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5"
            >
              <span
                className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${getTypeColor(file)}`}
              >
                <PaperClipIcon className="size-2.5" />
                {ext}
              </span>
              <span className="text-gray-700 hover:text-gray-900 hover:underline">
                {file.file_name}
              </span>
            </a>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                confirmDelete(file);
              }}
              aria-label={`Delete ${file.file_name}`}
              className="inline-flex size-4 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <XMarkIcon className="size-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
